import { useLayoutEffect, useRef } from 'react';

/** Window scroll position per page key (a route path). A key never seen starts at the top. */
const memory = new Map<string, number>();
const SETTLE_MS = 400; // after a page switch the browser clamps the old offset to the new height; ignore those events

export const peekScroll = (key: string): number => memory.get(key) ?? 0;
export const forgetScroll = (key: string): void => void memory.delete(key);

/**
 * Remember where the user was on each page and put them back there when they return; a page they have not
 * been to opens at the top. Call once at the app shell with the current route path as the key. The old page's
 * offset is captured the moment the key changes (before the new page is in the DOM, so nothing has clamped it
 * yet), and the new page's offset is restored before paint, so a swipe lands on the remembered position instead
 * of starting at the top and jumping. Content still loading may be shorter than the offset: the restore retries.
 */
export function useScrollMemory(key: string): void {
  const prev = useRef(key);
  const switchedAt = useRef(0);
  if (prev.current !== key && typeof window !== 'undefined') {
    memory.set(prev.current, window.scrollY);
    prev.current = key;
    switchedAt.current = Date.now();
  }
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    const target = memory.get(key) ?? 0;
    window.scrollTo({ top: target, behavior: 'auto' });
    const tries = [60, 180, 400].map((ms) =>
      setTimeout(() => {
        if (Math.abs(window.scrollY - target) > 2 && document.documentElement.scrollHeight - window.innerHeight >= target) window.scrollTo({ top: target, behavior: 'auto' });
      }, ms),
    );
    const onScroll = () => {
      if (Date.now() - switchedAt.current > SETTLE_MS) memory.set(key, window.scrollY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      tries.forEach(clearTimeout);
      window.removeEventListener('scroll', onScroll);
    };
  }, [key]);
}
